import { Fragment, useEffect } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import Drawer from '@mui/material/Drawer'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { useGetRecentImportQuery } from 'app/services/api/datamanagement/recentImport'
import { downloadFileFromDetailViewImport } from 'common/utils/exportUtils'

interface IImportDetailSlideout {
  isOpen: boolean
  importId: number | undefined
  closeSlideout: () => void
}

const ImportDetailSlideout = ({
  isOpen,
  importId,
  closeSlideout
}: IImportDetailSlideout) => {
  const { currentData: importDetail, refetch: refetchImportDetails } =
    useGetRecentImportQuery(importId ?? skipToken)

  useEffect(() => {
    if (isOpen) {
      refetchImportDetails()
    }
  }, [isOpen, refetchImportDetails])

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={closeSlideout}
      ModalProps={{
        BackdropProps: {
          style: { opacity: 0 }
        }
      }}
      PaperProps={{
        sx: { width: '33%' }
      }}
    >
      <div>
        <div className='flex border-b-2 border-gray-300 px-6 py-4'>
          <div className='font-bold'>Import Details</div>
          <div className='flex-1' />
          <button onClick={closeSlideout}>
            <CloseIcon fontSize='small' />
          </button>
        </div>
        <div className='px-6 pb-6 pt-8'>
          <div className='grid grid-flow-row grid-cols-[1fr,2fr] gap-x-8'>
            <div className='font-medium'>ID</div>
            <div>{importId}</div>
            <hr className='col-span-2 my-2 border-gray-300' />
            <div className='font-medium'>Imported By</div>
            <div>{importDetail?.importedBy ?? ''}</div>
            <hr className='col-span-2 my-2 border-gray-300' />
            <div className='font-medium'>Import Date</div>
            <div>
              {importDetail?.importDate != null
                ? new Date(importDetail?.importDate).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  })
                : ''}
            </div>
            <hr className='col-span-2 my-2 border-gray-300' />
            <div className='font-medium'>Status</div>
            <div>{importDetail?.importStatus ?? ''}</div>
          </div>
          <div
            className={[
              'pb-8 pt-14 font-medium',
              'uppercase tracking-[0.03em] text-gray-500'
            ].join(' ')}
          >
            Import Content
          </div>
          <div className='grid grid-cols-[1fr,2fr] gap-x-8'>
            <div className='font-medium'>Event Tables & Sources</div>
            <div>
              {importDetail?.fileDetails.map((i) => {
                return (
                  <div key={`${i.fileName}-${i.eventTable}`}>
                    <div>
                      <div className='flex items-center justify-between'>
                        <div>
                          <span className='font-normal leading-none text-gray-900'>
                            {i.eventTable}
                          </span>
                          <br />
                          <span className='leading-none text-gray-900'>
                            {i.fileName}
                          </span>
                        </div>
                        {importDetail.importStatus.toLowerCase() ===
                          'validated' && (
                          <button
                            onClick={() => {
                              downloadFileFromDetailViewImport(
                                importDetail.id as unknown as number,
                                i.fileName
                              )
                            }}
                          >
                            <OpenInNewIcon
                              className='text-xl text-lighthouseBlue'
                              sx={{ fontSize: '1.125rem' }}
                            />
                          </button>
                        )}
                      </div>
                      <hr className='my-2 border-gray-300' />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          <div
            className={[
              'pb-8 pt-14 font-medium uppercase',
              'tracking-[0.03em] text-gray-500'
            ].join(' ')}
          >
            ERROR LOG
          </div>
          <div className='grid grid-flow-row grid-cols-3'>
            <span className='font-medium'>File Name</span>
            <span className='font-medium'>Event Table</span>
            <span className='font-medium'>Record Number</span>
            <hr className='col-span-3 my-4 mt-2 border-gray-300' />
            {importDetail?.fileDetails
              .filter((fileData) => fileData.fileErrors.length > 0)
              .map((fileData) => (
                <Fragment key={`${fileData.fileName}-${fileData.eventTable}`}>
                  {fileData.fileErrors.map((error) => {
                    return (
                      <Fragment
                        key={`${fileData.fileName}-${fileData.eventTable}-${error.recordNumber}-${error.columnName}`}
                      >
                        <span className='mr-2 break-all leading-4 text-gray-900'>
                          {fileData.fileName}
                        </span>
                        <span className='mr-2 leading-4 text-gray-900'>
                          {fileData.eventTable}
                        </span>
                        <span className='leading-4 text-gray-900'>
                          Row {error.recordNumber} : {error.columnName}
                        </span>
                        <div
                          className={[
                            'col-span-3 mt-3 border p-2 text-sm',
                            'rounded-sm border-gray-300 bg-gray-100'
                          ].join(' ')}
                        >
                          <span className='text-red'>
                            {error.errorDescription}
                          </span>
                        </div>
                        <hr className='col-span-3 my-4 border-gray-300' />
                      </Fragment>
                    )
                  })}
                </Fragment>
              ))}
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default ImportDetailSlideout
